'use client';

import { motion } from 'framer-motion';
import { CTA } from '@/components/CTA/CTA';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';

interface TutorialVideoProps {
	heading?: string;
	description?: string;
	video_url?: string;
	CTAs: components['schemas']['SharedButtonComponent'][];
	isFirst?: boolean;
}

export default function TutorialVideo({
	heading = 'How To Send Bulk SMS',
	description = 'Discover how the Mtalkz Bulk SMS Platform can transform your business communication. Our official tutorial video guides you through setting up your account, creating impactful SMS campaigns, and utilizing advanced features like scheduling and personalization.',
	video_url,
	CTAs,
	isFirst = false,
}: TutorialVideoProps) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<section className="tutorial-video-section bg-gradient-to-b from-gray-50 via-gray-50 to-white px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
			<noscript>
				<style>{`
					.tutorial-video-section h2[style],
					.tutorial-video-section h3[style],
					.tutorial-video-section p[style],
					.tutorial-video-section div[style*='opacity'],
					.tutorial-video-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<div className="grid grid-cols-1 items-center gap-10 md:gap-14 lg:grid-cols-2 lg:gap-20 xl:gap-24">
					{/* Video Column */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: '-100px' }}
						transition={{
							duration: 0.5,
							ease: [0.25, 0.1, 0.25, 1],
						}}
						className="relative aspect-video w-full overflow-hidden rounded-xl bg-gray-100 shadow-[0_10px_25px_-12px_rgba(0,0,0,0.15)] ring-1 ring-gray-200/70 lg:rounded-2xl">
						<div className="absolute inset-0 bg-gradient-to-br from-gray-900/5 to-transparent opacity-50" />
						<iframe
							src={`${video_url}`}
							title="Mtalkz Bulk SMS Platform Tutorial"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
							className="absolute inset-0 h-full w-full"
						/>
					</motion.div>

					{/* Content Column */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: '-100px' }}
						transition={{
							delay: 0.2,
							duration: 0.5,
							ease: [0.25, 0.1, 0.25, 1],
						}}
						className="flex flex-col space-y-7 md:space-y-8">
						<div className="space-y-2">
							<SectionHeading className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
								{heading}
							</SectionHeading>
						</div>
						<p className="text-lg leading-relaxed text-gray-600 md:text-xl">
							{description}
						</p>

						<div className="flex flex-wrap gap-4 pt-3">
							<CTA CTAs={CTAs} />
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
